import {
  ChangeEvent,
  ClipboardEvent,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { DialogProps } from "@mui/material/Dialog";
import { useParams } from "react-router-dom";
import {
  Box,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import MetaCell from "../../../components/forms/MetaCell";
import {
  useCreateCondition,
  useCreateCells,
} from "../../../hooks/mutations/useSpecMutations";
import ConditionDatePicker from "../../../features/conditions/ConditionDatePicker";
import Delete from "../../../icons/Delete";
import { CHANNEL_POOL_EXPLAINER } from "../../../utils/labels";
import colors from "../../../theme/colors";

const MODE_CONFIG: Record<ModifyMode, ModifyModeConfig> = {
  duplicate: {
    showCountInput: true,
    showDelete: true,
    nameEditable: true,
    showReplicates: false,
    showSubmitFields: false,
    showPool: false,
    canGrowRows: true,
  },
  "add-replicates": {
    showCountInput: false,
    showDelete: false,
    nameEditable: false,
    showReplicates: true,
    showSubmitFields: false,
    showPool: false,
    canGrowRows: false,
  },
  submit: {
    showCountInput: false,
    showDelete: false,
    nameEditable: false,
    showReplicates: true,
    showSubmitFields: true,
    showPool: true,
    canGrowRows: false,
  },
};

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const POOL_COLUMN_WIDTH = 240;

const ModalContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const ModalFooter = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const RowsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const Row = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "showDelete" && prop !== "showReplicates" && prop !== "showPool",
})<{ showDelete?: boolean; showReplicates?: boolean; showPool?: boolean }>(
  ({ theme, showDelete, showReplicates, showPool }) => ({
    display: "grid",
    gridTemplateColumns: [
      "1fr",
      showPool ? `${POOL_COLUMN_WIDTH}px` : null,
      showReplicates ? "140px" : null,
      showDelete ? "auto" : null,
    ]
      .filter(Boolean)
      .join(" "),
    // Top-align when the pool column is shown so per-field error text doesn't
    // shift neighbouring inputs.
    alignItems: showPool ? "start" : "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
  })
);

const ColumnHeaderRow = styled(Row)({
  alignItems: "center",
  paddingBottom: 0,
});

const ColumnHeader = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  justifySelf: "flex-end",
  padding: theme.spacing(0.5),
  color: "#d32f2f",
  "&:hover": {
    backgroundColor: "rgba(211, 47, 47, 0.04)",
  },
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const outlinedLabelProps = {
  sx: {
    backgroundColor: "#fff",
    px: 1,
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
  },
};

const FIELD_HEIGHT = 40;

const fieldHeightSx = {
  "& .MuiOutlinedInput-root": {
    height: FIELD_HEIGHT,
  },
  "& .MuiOutlinedInput-input": {
    paddingTop: 0,
    paddingBottom: 0,
  },
};

const MAX_DUPLICATES = 100;

const poolFieldSx = {
  height: FIELD_HEIGHT,
  display: "flex",
  alignItems: "center",
  "& .MuiAutocomplete-root .MuiOutlinedInput-root.MuiAutocomplete-inputRoot": {
    height: FIELD_HEIGHT,
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: "4px",
    backgroundColor: "#fff",
    boxSizing: "border-box",
    "&:not(.Mui-focused):not(.Mui-error)": {
      border: `1px solid ${colors.rules}`,
    },
  },
};

const priorityOptions = [1, 2, 3, 4];
const locationOptions = [
  "30 Dane Full Scale",
  "30 Dane Sub Scale",
  "200 Inner Belt Lab 1",
];

const createEmptyErrors = (count: number): ModifyRowError[] =>
  Array.from({ length: count }, () => ({}));

const defaultCopyName = (base: string, index: number) =>
  index === 0 ? `${base} (copy)` : `${base} (copy ${index + 1})`;

const createDefaultSubmitDetails = (): ModifySubmitDetails => ({
  planned_start_date: null,
  priority: 4,
  location: null,
  notes: null,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ModifyConditionsModal = (props: ModifyConditionsModalProps) => {
  const { open, onClose, mode } = props;
  const config = MODE_CONFIG[mode];

  const { exp_id } = useParams();
  const createConditionMutation = useCreateCondition();
  const createCellsMutation = useCreateCells();

  const [rows, setRows] = useState<ModifyRow[]>([]);
  const [rowErrors, setRowErrors] = useState<ModifyRowError[]>([]);
  const [visibleRowCount, setVisibleRowCount] = useState(0);
  const [duplicateCountInput, setDuplicateCountInput] = useState("1");
  const [countError, setCountError] = useState<string>();
  const [submitError, setSubmitError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitDetails, setSubmitDetails] = useState<ModifySubmitDetails>(
    createDefaultSubmitDetails()
  );

  // Derived per-mode data ---------------------------------------------------

  const seedData = mode === "duplicate" ? props.seedData : undefined;
  const baseName = seedData?.sourceName?.trim() || "Condition";
  const basePayload = seedData?.basePayload;

  const conditions = mode === "duplicate" ? undefined : props.conditions ?? [];

  const parsedExpId = exp_id ? parseInt(exp_id, 10) : Number.NaN;
  const hasValidExpId = Number.isFinite(parsedExpId) && parsedExpId > 0;

  const isPending =
    isSubmitting ||
    createConditionMutation.isPending ||
    createCellsMutation.isPending;

  // Initialize rows whenever the modal opens -------------------------------

  // Tracks whether the modal was already open on the previous effect run, so
  // a `conditions` refetch while open (e.g. after a partially failed
  // add-replicates) doesn't wipe the error message or the user's inputs.
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!open) {
      return;
    }

    if (justOpened) {
      setCountError(undefined);
      setSubmitError(undefined);
    }

    if (mode === "duplicate") {
      setDuplicateCountInput("1");
      if (!seedData) {
        setRows([]);
        setRowErrors([]);
        setVisibleRowCount(0);
        return;
      }
      setRows([{ name: defaultCopyName(baseName, 0), replicates: "" }]);
      setRowErrors(createEmptyErrors(1));
      setVisibleRowCount(1);
      return;
    }

    // add-replicates + submit: one fixed row per selected condition.
    const conditionRows: ModifyRow[] = (conditions ?? []).map((condition) => ({
      name: condition.name,
      // Add-replicates seeds the current count; create mode defaults to 1.
      replicates:
        mode === "add-replicates" ? String(condition.replicates) : "1",
      conditionId: condition.cell_condition_id,
      currentReplicates: condition.replicates,
      // Create mode prefills any pool already saved on the condition.
      pool: mode === "submit" ? condition.pool ?? null : undefined,
    }));

    if (justOpened) {
      setRows(conditionRows);
    } else {
      // Conditions refetched while open: keep the user's entered target for
      // rows whose current count didn't change (e.g. rows that failed to
      // update); rows that did update reseed from the new current count.
      // Entered pools are always preserved.
      setRows((prev) =>
        conditionRows.map((row) => {
          const previous = prev.find(
            (candidate) => candidate.conditionId === row.conditionId
          );
          if (!previous) {
            return row;
          }
          const merged = { ...row, pool: previous.pool ?? row.pool };
          return previous.currentReplicates === row.currentReplicates
            ? { ...merged, replicates: previous.replicates }
            : merged;
        })
      );
    }
    setRowErrors(createEmptyErrors(conditionRows.length));
    setVisibleRowCount(conditionRows.length);

    if (mode === "submit" && justOpened) {
      setSubmitDetails({
        ...createDefaultSubmitDetails(),
        ...(props.initialDetails ?? {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, seedData, baseName, conditions]);

  // Row count management (duplicate only) ----------------------------------

  const syncErrorLength = (nextCount: number) => {
    if (nextCount <= 0) {
      return;
    }
    setRowErrors((prev) => {
      if (prev.length >= nextCount) {
        return prev;
      }
      return [...prev, ...createEmptyErrors(nextCount - prev.length)];
    });
  };

  const updateRowCount = (desiredCount: number) => {
    const nextCount = Math.max(0, Math.min(desiredCount, MAX_DUPLICATES));

    setRows((prev) => {
      if (nextCount <= prev.length) {
        return prev;
      }
      const additions = Array.from(
        { length: nextCount - prev.length },
        (_, offset) => ({
          name: defaultCopyName(baseName, prev.length + offset),
          replicates: "",
        })
      );
      return [...prev, ...additions];
    });

    syncErrorLength(nextCount);
    setVisibleRowCount(nextCount);
  };

  const handleDuplicateCountChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    if (!/^\d*$/.test(rawValue)) {
      return;
    }

    if (!rawValue) {
      setDuplicateCountInput("");
      setCountError("Enter the number of duplicates");
      setVisibleRowCount(0);
      return;
    }

    const parsedValue = parseInt(rawValue, 10);
    if (Number.isNaN(parsedValue) || parsedValue < 1) {
      setDuplicateCountInput(rawValue);
      setCountError("Must be at least 1");
      return;
    }

    let boundedValue = parsedValue;
    if (parsedValue > MAX_DUPLICATES) {
      boundedValue = MAX_DUPLICATES;
      setCountError(`Maximum of ${MAX_DUPLICATES} duplicates`);
    } else {
      setCountError(undefined);
    }

    setDuplicateCountInput(String(boundedValue));
    updateRowCount(boundedValue);
  };

  // Shared multi-line paste ------------------------------------------------

  // Spreads a multi-line clipboard paste across sequential rows starting at
  // `index`. When `canGrowRows` is true (duplicate mode) new rows are appended
  // up to MAX_DUPLICATES; otherwise the paste only fills existing rows.
  const applyMultiLinePaste = (
    index: number,
    event: ClipboardEvent<HTMLElement>,
    field: "name" | "replicates",
    transform: (value: string) => string
  ) => {
    const clipboardText = event.clipboardData?.getData("text");
    if (!clipboardText) {
      return;
    }

    const normalized = clipboardText.replace(/\r\n?/g, "\n");
    const rawLines = normalized.split("\n");
    const containsTab = rawLines.some((line) => line.includes("\t"));
    if (rawLines.length <= 1 && !containsTab) {
      return;
    }

    event.preventDefault();

    if (!config.canGrowRows) {
      // Fixed row set: only fill existing rows.
      setRows((prev) => {
        const next = [...prev];
        rawLines.forEach((line, offset) => {
          const targetIndex = index + offset;
          if (targetIndex >= next.length) {
            return;
          }
          const value = transform(line.trim());
          if (value !== "") {
            next[targetIndex] = { ...next[targetIndex], [field]: value };
          }
        });
        return next;
      });

      const impacted = new Set<number>();
      rawLines.forEach((_, offset) => impacted.add(index + offset));
      setRowErrors((prev) =>
        prev.map((error, rowIndex) => (impacted.has(rowIndex) ? {} : error))
      );
      return;
    }

    // Growable row set (duplicate mode).
    const availableSlots = Math.max(0, MAX_DUPLICATES - index);
    if (!availableSlots) {
      return;
    }
    const lines = rawLines.slice(0, availableSlots);
    if (!lines.length) {
      return;
    }

    const impactedIndices = new Set<number>();
    lines.forEach((_, offset) => impactedIndices.add(index + offset));

    let nextDesiredCount = rows.length;

    setRows((prev) => {
      const computedCount = Math.min(
        MAX_DUPLICATES,
        Math.max(prev.length, index + lines.length)
      );
      nextDesiredCount = computedCount;

      const nextRows: ModifyRow[] = Array.from(
        { length: computedCount },
        (_, rowIndex) => {
          if (rowIndex < prev.length) {
            return { ...prev[rowIndex] };
          }
          return {
            name: defaultCopyName(baseName, rowIndex),
            replicates: "",
          };
        }
      );

      lines.forEach((line, offset) => {
        const targetIndex = index + offset;
        if (targetIndex >= nextRows.length) {
          return;
        }
        nextRows[targetIndex] = {
          ...nextRows[targetIndex],
          [field]: transform(line.trim()),
        };
      });

      return nextRows;
    });

    setRowErrors((prev) =>
      Array.from({ length: nextDesiredCount }, (_, rowIndex) => {
        if (impactedIndices.has(rowIndex)) {
          return {};
        }
        return rowIndex < prev.length ? { ...prev[rowIndex] } : {};
      })
    );

    setDuplicateCountInput(String(nextDesiredCount));
    setVisibleRowCount(nextDesiredCount);
    setCountError(
      rawLines.length > lines.length
        ? `Maximum of ${MAX_DUPLICATES} duplicates`
        : undefined
    );
  };

  // Field editing ----------------------------------------------------------

  const clearRowError = (index: number, field: keyof ModifyRowError) => {
    if (rowErrors[index]?.[field]) {
      setRowErrors((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: undefined };
        return next;
      });
    }
  };

  const handleNameChange = (index: number, value: string) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, name: value } : row
      )
    );
    clearRowError(index, "name");
  };

  const handleReplicatesChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) {
      return;
    }
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, replicates: value } : row
      )
    );
    clearRowError(index, "replicates");
  };

  const handlePoolChange = (index: number, value: string | null) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, pool: value || null } : row
      )
    );
    clearRowError(index, "pool");
  };

  // Copies the first row's channel pool to every visible row.
  const firstRowPool = rows[0]?.pool ?? null;
  const canApplyPoolToAll =
    config.showPool && !!firstRowPool && visibleRowCount > 1;

  const handleApplyPoolToAll = () => {
    if (!firstRowPool) {
      return;
    }
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex < visibleRowCount ? { ...row, pool: firstRowPool } : row
      )
    );
    setRowErrors((prev) =>
      prev.map((error) => ({ ...error, pool: undefined }))
    );
  };

  const handleNamePaste = (index: number, event: ClipboardEvent<HTMLElement>) =>
    applyMultiLinePaste(index, event, "name", (value) =>
      (value.split("\t")[0] ?? "").trim()
    );

  const handleReplicatesPaste = (
    index: number,
    event: ClipboardEvent<HTMLElement>
  ) =>
    applyMultiLinePaste(index, event, "replicates", (value) =>
      value.replace(/[^\d]/g, "")
    );

  const handleDeleteRow = (index: number) => {
    const nextVisibleCount = Math.max(visibleRowCount - 1, 0);
    setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
    setRowErrors((prev) => prev.filter((_, rowIndex) => rowIndex !== index));

    if (nextVisibleCount > 0) {
      setDuplicateCountInput(String(nextVisibleCount));
      setCountError(undefined);
    } else {
      setDuplicateCountInput("");
      setCountError("Enter the number of duplicates");
    }
    setVisibleRowCount(nextVisibleCount);
  };

  const updateSubmitDetail = (updates: Partial<ModifySubmitDetails>) => {
    setSubmitDetails((prev) => ({ ...prev, ...updates }));
  };

  // Validation -------------------------------------------------------------

  const validateForm = () => {
    const activeRows = rows.slice(0, visibleRowCount);
    const errors = createEmptyErrors(activeRows.length);
    let hasError = false;

    if (mode === "duplicate") {
      if (!seedData || !basePayload) {
        setSubmitError("Missing condition details required for duplication.");
        return false;
      }
      if (!duplicateCountInput || duplicateCountInput === "0") {
        setCountError("Must be at least 1");
        return false;
      }
      if (countError) {
        return false;
      }

      const namesSeen = new Map<string, number>();
      activeRows.forEach((row, index) => {
        const trimmedName = row.name.trim();
        if (!trimmedName) {
          errors[index].name = "Name is required";
          hasError = true;
          return;
        }
        const normalized = trimmedName.toLowerCase();
        if (namesSeen.has(normalized)) {
          const duplicateIndex = namesSeen.get(normalized)!;
          errors[index].name = "Name must be unique";
          errors[duplicateIndex].name = "Name must be unique";
          hasError = true;
        } else {
          namesSeen.set(normalized, index);
        }
      });
    } else {
      // add-replicates + submit: validate replicate target values.
      const minAllowed = mode === "add-replicates";
      activeRows.forEach((row, index) => {
        if (mode === "submit" && !row.pool?.trim()) {
          errors[index].pool = "Channel pool is required";
          hasError = true;
        }
        const raw = row.replicates.trim();
        const parsed = parseInt(raw, 10);
        if (raw === "" || Number.isNaN(parsed)) {
          errors[index].replicates = "Enter a number";
          hasError = true;
          return;
        }
        const current = row.currentReplicates ?? 0;
        if (minAllowed && parsed < current) {
          errors[index].replicates = `Cannot be less than ${current}`;
          hasError = true;
        } else if (parsed < 0) {
          errors[index].replicates = "Cannot be negative";
          hasError = true;
        }
      });
    }

    setRowErrors(errors);
    return !hasError;
  };

  // Submit handlers per mode -----------------------------------------------

  const handleDuplicateSubmit = async () => {
    if (!seedData || !basePayload) {
      setSubmitError("Missing condition details required for duplication.");
      return;
    }
    if (!hasValidExpId) {
      setSubmitError("Unable to determine experiment context.");
      return;
    }

    const createdConditionIds: number[] = [];
    let encounteredError = false;

    for (const row of rows.slice(0, visibleRowCount)) {
      try {
        const response = await createConditionMutation.mutateAsync({
          ...basePayload,
          name: row.name.trim(),
          exp_id: parsedExpId,
        });
        const createdId = response?.cell_condition_id;
        if (typeof createdId === "number") {
          createdConditionIds.push(createdId);
        } else {
          encounteredError = true;
          setSubmitError("Condition was created without an identifier.");
          break;
        }
      } catch (error) {
        console.error("Error duplicating condition:", error);
        encounteredError = true;
        setSubmitError("Unable to duplicate condition. Please try again.");
        break;
      }
    }

    if (createdConditionIds.length && mode === "duplicate") {
      props.onSubmit({ createdConditionIds });
    }
    if (!encounteredError) {
      onClose();
    }
  };

  const handleAddReplicatesConfirm = async () => {
    if (mode !== "add-replicates") {
      return;
    }
    // Compute the delta from current -> target; skip no-op rows.
    const results: AddReplicatesResult[] = rows
      .slice(0, visibleRowCount)
      .map((row) => {
        const current = row.currentReplicates ?? 0;
        const target = parseInt(row.replicates, 10);
        return {
          conditionId: row.conditionId!,
          currentNumberOfReplicates: current,
          numberOfReplicates: target - current,
        };
      })
      .filter((result) => result.numberOfReplicates > 0);

    try {
      // Parent closes the modal on success; a throw keeps it open.
      await props.onConfirm(results);
    } catch (error) {
      setSubmitError(
        error instanceof Error && error.message
          ? error.message
          : "Unable to add replicates. Please try again."
      );
    }
  };

  const handleSubmitConditions = async () => {
    if (mode !== "submit") {
      return;
    }

    const conditions = rows.slice(0, visibleRowCount).map((row) => ({
      cellConditionId: row.conditionId!,
      replicates: parseInt(row.replicates, 10),
      pool: row.pool!.trim(),
    }));

    try {
      await createCellsMutation.mutateAsync({
        conditions,
        planned_start_date: submitDetails.planned_start_date,
        priority: submitDetails.priority,
        location: submitDetails.location,
        notes: submitDetails.notes,
      });
      props.onSubmit({
        submittedConditionIds: conditions.map((c) => c.cellConditionId),
      });
      onClose();
    } catch (error) {
      console.error("Error submitting conditions for cells:", error);
      const message = "Unable to submit conditions. Please try again.";
      setSubmitError(message);
      props.onError?.(message);
    }
  };

  const handleConfirm = async () => {
    setSubmitError(undefined);
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === "duplicate") {
        await handleDuplicateSubmit();
      } else if (mode === "add-replicates") {
        await handleAddReplicatesConfirm();
      } else {
        await handleSubmitConditions();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Presentation helpers ---------------------------------------------------

  const { title, subtitle, confirmLabel, loadingLabel } = useMemo(() => {
    switch (mode) {
      case "duplicate":
        return {
          title: `Duplicate ${seedData?.sourceName || "condition"}`,
          subtitle: undefined,
          confirmLabel: "DUPLICATE CONDITION",
          loadingLabel: "CREATING...",
        };
      case "add-replicates":
        return {
          title: "Add Replicates",
          subtitle:
            "Enter the target number of replicates for each condition. You can paste a column of numbers to fill multiple rows at once.",
          confirmLabel: "ADD REPLICATES",
          loadingLabel: "ADDING...",
        };
      case "submit":
      default: {
        return {
          title: "Create Cells",
          subtitle:
            'Priority, location, date, and notes apply to all selected conditions. Set a channel pool and replicate count for each condition. The resulting cells will be created with the "Staged" status.',
          confirmLabel: "CREATE CELLS",
          loadingLabel: "SUBMITTING...",
        };
      }
    }
  }, [mode, seedData]);

  const disableConfirm = useMemo(() => {
    if (isPending) {
      return true;
    }
    if (visibleRowCount === 0) {
      return true;
    }
    if (mode === "duplicate") {
      return !seedData || !basePayload || !hasValidExpId;
    }
    return false;
  }, [isPending, visibleRowCount, mode, seedData, basePayload, hasValidExpId]);

  const handleModalClose: DialogProps["onClose"] = (event, reason) => {
    const syntheticEvent = event as SyntheticEvent;
    if (reason === "backdropClick") {
      return;
    }
    if (reason === "escapeKeyDown" && syntheticEvent?.type !== "click") {
      return;
    }
    onClose();
  };

  // Render -----------------------------------------------------------------

  return (
    <Modal open={open} onClose={handleModalClose} maxWidth="md">
      <ModalContent>
        <ModalHeader>
          <Typography variant="h6">{title}</Typography>
          {subtitle ? (
            <Typography
              variant="body2"
              color="text.secondary"
              style={{ marginBottom: 15 }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </ModalHeader>

        {config.showCountInput ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TextField
              label="Number of duplicates"
              variant="outlined"
              fullWidth
              value={duplicateCountInput}
              onChange={handleDuplicateCountChange}
              type="number"
              inputProps={{ min: 1, max: MAX_DUPLICATES }}
              disabled={isPending || !seedData}
              error={!!countError}
              helperText={countError}
              InputLabelProps={outlinedLabelProps}
              sx={{ flex: 1 }}
            />
          </Box>
        ) : null}

        {config.showSubmitFields ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(120px, 1fr) minmax(160px, 1fr) minmax(220px, 1.2fr) minmax(220px, 1.6fr)",
                gap: 2,
                alignItems: "center",
              }}
            >
              <TextField
                select
                size="small"
                variant="outlined"
                label="Priority"
                value={submitDetails.priority ?? 4}
                onChange={(event) =>
                  updateSubmitDetail({ priority: Number(event.target.value) })
                }
                InputLabelProps={outlinedLabelProps}
                sx={{ width: "100%", ...fieldHeightSx }}
              >
                {priorityOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                variant="outlined"
                // label="Location"
                value={submitDetails.location ?? ""}
                onChange={(event) =>
                  updateSubmitDetail({
                    location:
                      event.target.value === ""
                        ? null
                        : (event.target.value as string),
                  })
                }
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) =>
                    value === "" || value === null
                      ? "Select location"
                      : String(value),
                }}
                InputLabelProps={outlinedLabelProps}
                sx={{ width: "100%", ...fieldHeightSx }}
              >
                <MenuItem value="">Select location</MenuItem>
                {locationOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <Box
                sx={{
                  minWidth: 220,
                  width: "100%",
                  height: FIELD_HEIGHT,
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#ffffff",
                  border: "1px solid #E7E7E7",
                  borderRadius: 1,
                  boxSizing: "border-box",
                  "& .MuiInputBase-root": {
                    width: "100%",
                    height: "100%",
                    backgroundColor: "transparent",
                  },
                  "& .MuiInputAdornment-root": {
                    marginLeft: 0,
                    marginRight: "8px",
                  },
                  "& input": { padding: "0 14px" },
                }}
              >
                <ConditionDatePicker
                  name="condition-start-date"
                  value={submitDetails.planned_start_date}
                  onChange={(value) =>
                    updateSubmitDetail({ planned_start_date: value ?? null })
                  }
                  wrapInCell={false}
                />
              </Box>
              <TextField
                size="small"
                variant="outlined"
                label="Notes"
                placeholder="Add notes"
                value={submitDetails.notes ?? ""}
                multiline
                minRows={1}
                maxRows={3}
                onChange={(event) =>
                  updateSubmitDetail({
                    notes:
                      event.target.value.trim() === ""
                        ? null
                        : event.target.value,
                  })
                }
                InputLabelProps={outlinedLabelProps}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    minHeight: FIELD_HEIGHT,
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                }}
              />
            </Box>
          </Box>
        ) : null}

        <RowsContainer>
          {config.showPool && visibleRowCount > 0 ? (
            <ColumnHeaderRow
              showDelete={config.showDelete}
              showReplicates={config.showReplicates}
              showPool={config.showPool}
            >
              <ColumnHeader>Condition</ColumnHeader>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <ColumnHeader>Channel pool</ColumnHeader>
                {visibleRowCount > 1 ? (
                  <Tooltip
                    arrow
                    title={
                      canApplyPoolToAll
                        ? "Copy the first condition's channel pool to all conditions"
                        : "Select a channel pool for the first condition to apply it to all"
                    }
                  >
                    <span>
                      <Button
                        color="text"
                        size="small"
                        onClick={handleApplyPoolToAll}
                        disabled={!canApplyPoolToAll || isPending}
                        sx={{
                          minWidth: 0,
                          padding: "0 4px",
                          fontSize: 12,
                          lineHeight: "20px",
                          textTransform: "none",
                        }}
                      >
                        Apply to all
                      </Button>
                    </span>
                  </Tooltip>
                ) : null}
              </Box>
              {config.showReplicates ? (
                <ColumnHeader>Replicates</ColumnHeader>
              ) : null}
            </ColumnHeaderRow>
          ) : null}
          {rows.slice(0, visibleRowCount).map((row, index) => {
            const rowError = rowErrors[index] ?? {};
            return (
              <Row
                key={row.conditionId ?? index}
                showDelete={config.showDelete}
                showReplicates={config.showReplicates}
                showPool={config.showPool}
              >
                {config.nameEditable ? (
                  <TextField
                    label={`Duplicate name ${index + 1}`}
                    variant="outlined"
                    value={row.name}
                    onChange={(event) =>
                      handleNameChange(index, event.target.value)
                    }
                    onPaste={(event) => handleNamePaste(index, event)}
                    fullWidth
                    disabled={isPending}
                    error={!!rowError.name}
                    helperText={rowError.name}
                    multiline
                    minRows={1}
                    InputLabelProps={outlinedLabelProps}
                  />
                ) : (
                  <TextField
                    label="Condition name"
                    size="small"
                    variant="outlined"
                    value={row.name}
                    fullWidth
                    disabled
                    InputLabelProps={outlinedLabelProps}
                    sx={fieldHeightSx}
                  />
                )}

                {config.showPool ? (
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={poolFieldSx}>
                      <MetaCell
                        noTable
                        style={{ width: "100%" }}
                        endpoint="meta/channels/pool-ids"
                        searchKey="pool"
                        sortOptions
                        dropdownTooltipText={CHANNEL_POOL_EXPLAINER}
                        value={row.pool ?? null}
                        onChange={(value) => handlePoolChange(index, value)}
                        onBlur={() => undefined}
                        disabled={isPending}
                        error={!!rowError.pool}
                      />
                    </Box>
                    {rowError.pool ? (
                      <ErrorText variant="caption" sx={{ ml: "14px" }}>
                        {rowError.pool}
                      </ErrorText>
                    ) : null}
                  </Box>
                ) : null}

                {config.showReplicates ? (
                  <TextField
                    size="small"
                    variant="outlined"
                    value={row.replicates}
                    onChange={(event) =>
                      handleReplicatesChange(index, event.target.value)
                    }
                    onPaste={(event) => handleReplicatesPaste(index, event)}
                    disabled={isPending}
                    error={!!rowError.replicates}
                    helperText={rowError.replicates}
                    inputProps={{
                      "aria-label": `Replicates for ${row.name}`,
                      inputMode: "numeric",
                    }}
                    sx={fieldHeightSx}
                  />
                ) : null}

                {config.showDelete ? (
                  <DeleteButton
                    size="small"
                    onClick={() => handleDeleteRow(index)}
                    disabled={isPending}
                    aria-label={`Remove duplicate ${index + 1}`}
                  >
                    <Delete />
                  </DeleteButton>
                ) : null}
              </Row>
            );
          })}
        </RowsContainer>

        {submitError ? (
          <ErrorText variant="body2">{submitError}</ErrorText>
        ) : null}

        <ModalFooter>
          <Button color="secondary" onClick={onClose} disabled={isPending}>
            CANCEL
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={disableConfirm}
          >
            {isPending ? loadingLabel : confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModifyConditionsModal;
